/**
 * UnifiedTable.tsx — Category drill-down merging categories + subcategories +
 * line items, with inline discrepancy expansion. Three tabs switch the view
 * (category tree / flat all-items / discrepancies-only) and "Filtra" narrows by
 * severity. Wired to the live tender-api: categories + line items come from
 * useAuditCategories / useAuditLineItems (props), grouped by the category FK.
 */
import { Fragment, useState } from "react";

import { I } from "../../components/icons";
import { DropdownMenu } from "../../components/ui/DropdownMenu";
import { type AnalysisJob, type AuditCategory, type AuditLineItem } from "../../lib/tenderApi";
import { DiscrepancyContent } from "./DiscrepancyContent";
import { fmtEur, statusFromBackend } from "./helpers";

type View = "tree" | "all" | "discrepancies";
type Severity = "all" | "err" | "warn";

interface SharedRowProps {
  projectId: string;
  openRow: string | null;
  openDiscrepancy: (item: AuditLineItem) => void;
}

function Tab({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}): JSX.Element {
  return (
    <button
      onClick={onClick}
      style={{
        appearance: "none",
        border: "none",
        background: "transparent",
        padding: "12px 16px",
        fontSize: 12.5,
        fontWeight: 500,
        cursor: "pointer",
        color: active ? "var(--c-ink)" : "var(--c-ink-3)",
        borderBottom: active ? "2px solid var(--c-brand)" : "2px solid transparent",
        marginBottom: -1,
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
    >
      {children}
    </button>
  );
}

function LineItemRow({
  item,
  projectId,
  openRow,
  openDiscrepancy,
}: { item: AuditLineItem } & SharedRowProps): JSX.Element {
  const isOpen = openRow === item.id;
  const hasD = !!item.discrepancy;
  const status = hasD ? statusFromBackend(item.discrepancy!.status) : null;
  const sevColor = item.discrepancy?.severity === "err" ? "var(--c-err)" : "var(--c-warn)";

  return (
    <Fragment>
      <tr
        onClick={() => hasD && openDiscrepancy(item)}
        style={{
          cursor: hasD ? "pointer" : "default",
          background: isOpen ? "var(--c-warn-soft)" : undefined,
        }}
      >
        <td>{hasD && <I.Alert size={13} style={{ color: sevColor }} />}</td>
        <td className="t-mono" style={{ color: "var(--c-brand)", fontSize: 11.5, fontWeight: 500 }}>
          {item.code ?? item.id.slice(0, 8)}
        </td>
        <td style={{ paddingLeft: 24, color: "var(--c-ink)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 460,
                display: "inline-block",
              }}
            >
              {item.description}
            </span>
            {status === "Risolto" && (
              <span className="pill pill-ok">
                <I.Check size={10} />
                Risolto
              </span>
            )}
            {status === "In revisione" && (
              <span className="pill pill-info">
                <I.Eye size={10} />
                In revisione
              </span>
            )}
            {status === "Non risolto" && <span className="pill pill-err">Non risolto</span>}
          </div>
        </td>
        <td className="t-num" style={{ textAlign: "right" }}>
          {item.qty.toLocaleString("it-IT")}{" "}
          <span className="muted" style={{ fontSize: 11 }}>
            {item.unit}
          </span>
        </td>
        <td className="t-num" style={{ textAlign: "right" }}>
          € {item.unitPrice.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
        </td>
        <td className="t-num" style={{ textAlign: "right", fontWeight: 500 }}>
          {fmtEur(item.total)}
        </td>
        <td />
        <td className="t-num" style={{ textAlign: "right", color: "var(--c-ink-3)" }}>
          {item.pctBudget != null ? `${item.pctBudget.toFixed(2)}%` : "—"}
        </td>
        <td>
          {hasD && (
            <button
              className="btn btn-ghost btn-icon btn-sm"
              onClick={(e) => {
                e.stopPropagation();
                openDiscrepancy(item);
              }}
            >
              <I.Chevron size={13} style={{ transform: isOpen ? "rotate(90deg)" : "none" }} />
            </button>
          )}
        </td>
      </tr>

      {isOpen && item.discrepancy && (
        <tr>
          <td
            colSpan={9}
            style={{
              padding: 0,
              background: "var(--c-warn-soft)",
              borderTop: "1px solid rgba(184,115,12,0.20)",
              borderBottom: "1px solid rgba(184,115,12,0.20)",
            }}
          >
            <DiscrepancyContent item={item} projectId={projectId} />
          </td>
        </tr>
      )}
    </Fragment>
  );
}

interface UnifiedTableProps extends SharedRowProps {
  categories: AuditCategory[];
  lineItems: AuditLineItem[];
  loading: boolean;
  error: string | null;
  expandedCats: Set<string>;
  toggleCat: (id: string) => void;
  /** Latest analyze job for the project (null = never analyzed) — drives the
   * empty state: live progress while it runs, failure + retry, or a start CTA. */
  analysisJob?: AnalysisJob | null;
  onStartAnalysis?: () => void;
  startPending?: boolean;
  startError?: string | null;
}

const SEVERITY_LABEL: Record<Severity, string> = {
  all: "Tutte le severità",
  err: "Solo critiche",
  warn: "Solo da rivedere",
};

export function UnifiedTable({
  categories,
  lineItems,
  loading,
  error,
  expandedCats,
  toggleCat,
  analysisJob,
  onStartAnalysis,
  startPending,
  startError,
  ...rowProps
}: UnifiedTableProps): JSX.Element {
  const [view, setView] = useState<View>("tree");
  const [severity, setSeverity] = useState<Severity>("all");

  const cats = categories.filter((c) => !c.isUncategorized);
  const discrepancyCount = lineItems.filter((i) => i.discrepancy != null).length;
  const grandTotal = lineItems.reduce((s, i) => s + i.total, 0);

  const matchesSeverity = (item: AuditLineItem): boolean =>
    severity === "all" ? true : item.discrepancy?.severity === severity;

  /** ids that belong to a top-level category (itself + its subcategories). */
  const catIdSet = (cat: AuditCategory): Set<string> =>
    new Set<string>([cat.id, ...cat.subs.map((s) => s.id)]);

  const flatItems =
    view === "discrepancies"
      ? lineItems.filter((i) => i.discrepancy != null && matchesSeverity(i))
      : lineItems.filter(matchesSeverity);

  const colSpan = 9;

  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div style={{ display: "flex", borderBottom: "1px solid var(--c-line)" }}>
        <Tab active={view === "tree"} onClick={() => setView("tree")}>
          Categorie &amp; voci
        </Tab>
        <Tab active={view === "all"} onClick={() => setView("all")}>
          Tutte le voci ({lineItems.length.toLocaleString("it-IT")})
        </Tab>
        <Tab active={view === "discrepancies"} onClick={() => setView("discrepancies")}>
          Solo discrepanze ({discrepancyCount})
        </Tab>
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "0 14px" }}>
          <DropdownMenu
            align="right"
            width={190}
            header="Filtra per severità"
            items={(["all", "err", "warn"] as Severity[]).map((s) => ({
              label: SEVERITY_LABEL[s],
              active: severity === s,
              onClick: () => setSeverity(s),
            }))}
            renderTrigger={(toggle) => (
              <button className="btn btn-ghost btn-sm" onClick={toggle}>
                <I.Filter size={12} /> Filtra{severity !== "all" ? " (1)" : ""}
              </button>
            )}
          />
        </div>
      </div>

      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 28 }}></th>
            <th style={{ width: 90 }}>ID</th>
            <th>Descrizione</th>
            <th style={{ textAlign: "right", width: 130 }}>Quantità</th>
            <th style={{ textAlign: "right", width: 110 }}>€ unitario</th>
            <th style={{ textAlign: "right", width: 130 }}>Totale</th>
            <th style={{ textAlign: "right", width: 90 }}>€/m²</th>
            <th style={{ textAlign: "right", width: 100 }}>% budget</th>
            <th style={{ width: 38 }}></th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td
                colSpan={colSpan}
                style={{ textAlign: "center", padding: 24, color: "var(--c-ink-3)" }}
              >
                Caricamento…
              </td>
            </tr>
          )}
          {!loading && error && (
            <tr>
              <td
                colSpan={colSpan}
                style={{ textAlign: "center", padding: 24, color: "var(--c-err)" }}
              >
                {error}
              </td>
            </tr>
          )}
          {!loading && !error && lineItems.length === 0 && (
            <tr>
              <td colSpan={colSpan} style={{ textAlign: "center", padding: 32 }}>
                {analysisJob && ["queued", "running"].includes(analysisJob.status) ? (
                  // A run is in flight (rediscovered even after reload/reconnect).
                  <div style={{ color: "var(--c-ink-2)" }}>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                      Analisi in corso… {analysisJob.progress}%
                    </div>
                    <div
                      style={{
                        fontSize: 12.5,
                        color: "var(--c-ink-3)",
                        maxWidth: 440,
                        margin: "0 auto",
                      }}
                    >
                      Tempo stimato: 1–3 ore. L&apos;analisi gira sul server: puoi chiudere la
                      pagina o spegnere il computer — i risultati appariranno qui appena pronta.
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
                      Questo progetto non è ancora stato analizzato
                    </div>
                    {analysisJob?.status === "failed" && (
                      <div
                        style={{
                          fontSize: 12.5,
                          color: "var(--c-err)",
                          marginBottom: 10,
                          maxWidth: 480,
                          margin: "0 auto 10px",
                        }}
                      >
                        L&apos;ultima analisi non è riuscita
                        {analysisJob.error ? `: ${analysisJob.error}` : "."}
                      </div>
                    )}
                    {startError && (
                      <div style={{ fontSize: 12.5, color: "var(--c-err)", marginBottom: 10 }}>
                        {startError}
                      </div>
                    )}
                    {onStartAnalysis && (
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={onStartAnalysis}
                        disabled={startPending}
                      >
                        {startPending ? "Avvio…" : "Avvia analisi AI"}
                      </button>
                    )}
                  </div>
                )}
              </td>
            </tr>
          )}

          {!loading && !error && lineItems.length > 0 && view === "tree" ? (
            <>
              {cats.map((cat) => {
                const isOpen = expandedCats.has(cat.id);
                const ids = catIdSet(cat);
                const itemsInCat = lineItems
                  .filter((t) => t.categoryId != null && ids.has(t.categoryId))
                  .filter(matchesSeverity);
                return (
                  <Fragment key={cat.id}>
                    {/* Category row */}
                    <tr
                      style={{ background: "var(--c-bg-sunken)", cursor: "pointer" }}
                      onClick={() => toggleCat(cat.id)}
                    >
                      <td>
                        <I.Chevron
                          size={12}
                          style={{
                            color: "var(--c-ink-3)",
                            transform: isOpen ? "rotate(90deg)" : "none",
                            transition: "transform var(--tr-fast)",
                          }}
                        />
                      </td>
                      <td className="t-mono" style={{ color: "var(--c-ink-3)", fontWeight: 600 }}>
                        {cat.code ?? ""}
                      </td>
                      <td style={{ fontWeight: 600, fontSize: 13 }}>{cat.name}</td>
                      <td />
                      <td />
                      <td
                        className="t-num"
                        style={{
                          textAlign: "right",
                          fontWeight: 600,
                          fontFamily: "var(--font-mono)",
                        }}
                      >
                        {cat.cost != null ? fmtEur(cat.cost) : "—"}
                      </td>
                      <td className="t-num" style={{ textAlign: "right", color: "var(--c-ink-2)" }}>
                        {cat.eurM2 != null ? `€ ${cat.eurM2.toFixed(0)}` : "—"}
                      </td>
                      <td className="t-num" style={{ textAlign: "right" }}>
                        {cat.pct != null ? `${cat.pct.toFixed(1)}%` : "—"}
                      </td>
                      <td />
                    </tr>

                    {/* Subcategories */}
                    {isOpen &&
                      cat.subs.map((sub) => (
                        <tr key={sub.id}>
                          <td />
                          <td
                            className="t-mono"
                            style={{ color: "var(--c-ink-3)", fontSize: 11.5 }}
                          >
                            {sub.code ?? ""}
                          </td>
                          <td style={{ color: "var(--c-ink-2)", paddingLeft: 24 }}>↳ {sub.name}</td>
                          <td />
                          <td />
                          <td className="t-num" style={{ textAlign: "right" }}>
                            {sub.cost != null ? fmtEur(sub.cost) : "—"}
                          </td>
                          <td
                            className="t-num"
                            style={{ textAlign: "right", color: "var(--c-ink-3)" }}
                          >
                            {sub.eurM2 != null ? `€ ${sub.eurM2.toFixed(0)}` : "—"}
                          </td>
                          <td
                            className="t-num"
                            style={{ textAlign: "right", color: "var(--c-ink-3)" }}
                          >
                            {sub.pct != null ? `${sub.pct.toFixed(1)}%` : "—"}
                          </td>
                          <td />
                        </tr>
                      ))}

                    {/* Line items in this category */}
                    {isOpen && itemsInCat.length > 0 && (
                      <tr>
                        <td
                          colSpan={colSpan}
                          style={{
                            background: "var(--c-bg)",
                            padding: "6px 14px 6px 50px",
                            fontSize: 10.5,
                            color: "var(--c-ink-3)",
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            fontWeight: 600,
                            borderBottom: "1px solid var(--c-line)",
                          }}
                        >
                          Voci in {cat.name}
                        </td>
                      </tr>
                    )}
                    {isOpen &&
                      itemsInCat.map((item) => (
                        <LineItemRow key={item.id} item={item} {...rowProps} />
                      ))}
                  </Fragment>
                );
              })}

              {/* Totals */}
              <tr style={{ background: "var(--c-bg-sunken)" }}>
                <td />
                <td />
                <td style={{ fontWeight: 600 }}>Totale CME</td>
                <td />
                <td />
                <td
                  className="t-num"
                  style={{
                    textAlign: "right",
                    fontWeight: 700,
                    fontFamily: "var(--font-mono)",
                    fontSize: 14,
                  }}
                >
                  {fmtEur(grandTotal)}
                </td>
                <td />
                <td className="t-num" style={{ textAlign: "right", fontWeight: 600 }}>
                  100%
                </td>
                <td />
              </tr>
            </>
          ) : !loading && !error && lineItems.length > 0 ? (
            <>
              {flatItems.map((item) => (
                <LineItemRow key={item.id} item={item} {...rowProps} />
              ))}
              {flatItems.length === 0 && (
                <tr>
                  <td
                    colSpan={colSpan}
                    style={{ textAlign: "center", padding: 24, color: "var(--c-ink-3)" }}
                  >
                    Nessuna voce corrisponde al filtro.
                  </td>
                </tr>
              )}
            </>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
